/**
 * Documents domain — hooks.
 *
 * Read hooks are pass-through re-exports of the generated hooks. Mutations are
 * wrapped through `useInvalidatingMutation` so the registry runs on success.
 * Upload/replace wrap the imperative multipart helpers from `utils.ts`.
 */

import { useInvalidatingMutation } from '../invalidation/useInvalidatingMutation'
import { invalidationRegistry } from './invalidation'
import {
  replaceDocument as replaceDocumentRequest,
  uploadDocument as uploadDocumentRequest,
  type DocumentId,
  type ReplaceDocumentParams,
  type UploadDocumentParams,
} from './utils'

// ── Read hooks (pass-through) ───────────────────────────────────────
export {
  useListDocuments,
  listDocumentsQueryKey,
  useGetDocumentQuota,
  useListDocumentTags,
} from '../generated/hooks/documentsController'

export type {
  ListDocumentsQueryParams,
  ListDocumentsQueryResponse,
} from '../generated/types/ListDocuments'
export type { UpdateDocumentMutationRequest } from '../generated/types/UpdateDocument'
export type { BulkAssignDocumentBrandMutationRequest } from '../generated/types/BulkAssignDocumentBrand'

// ── File extension options (schema-mismatch normalize, not a plain pass-through) ──
// `useListDocumentFileExtensions` isn't re-exported raw: its generated type
// doesn't match the wire format (see docstring below), so it's only used
// internally to build the safe `useGetFileExtensionOptions` wrapper.
import { useListDocumentFileExtensions } from '../generated/hooks/documentsController'
import type { ListDocumentFileExtensionsQueryResponse } from '../generated/types/ListDocumentFileExtensions'
import type { DocumentFileExtensionOption } from '../generated/types/DocumentFileExtensionOption'

/**
 * File-extension filter options. Keeps the legacy hook name used by
 * `DocumentsFilterset` / `DealDocumentsFilterset` for a smaller diff.
 *
 * NOT a plain pass-through: `GET /documents/file-extensions/` returns a
 * bare array at runtime (`file_extensions_view.py`'s `file_extensions`
 * action calls `Response(options)` directly — it never paginates), but
 * drf-spectacular's schema for that `@action(detail=False)` on
 * `DocumentViewSet` (a paginated `ModelViewSet`) still wraps the declared
 * `DocumentFileExtensionOptionSerializer(many=True)` response in the
 * paginator envelope, so the generated type says
 * `PaginatedDocumentFileExtensionOptionList` while the wire format is
 * `DocumentFileExtensionOption[]`. `select` normalizes both shapes so
 * this keeps working whichever way the schema annotation is eventually
 * fixed server-side.
 */
export function useGetFileExtensionOptions() {
  return useListDocumentFileExtensions(undefined, {
    query: {
      select: (
        data: ListDocumentFileExtensionsQueryResponse
      ): DocumentFileExtensionOption[] => {
        const raw = data as unknown
        if (Array.isArray(raw)) return raw as DocumentFileExtensionOption[]
        return (
          (raw as { results?: DocumentFileExtensionOption[] } | null)
            ?.results ?? []
        )
      },
    },
  })
}

// ── Generated mutation clients ──────────────────────────────────────
import { updateDocument } from '../generated/clients/updateDocument'
import { deleteDocument } from '../generated/clients/deleteDocument'
import { bulkAssignDocumentBrand } from '../generated/clients/bulkAssignDocumentBrand'
import type { UpdateDocumentMutationRequest } from '../generated/types/UpdateDocument'
import type { BulkAssignDocumentBrandMutationRequest } from '../generated/types/BulkAssignDocumentBrand'

// ── Wrapped mutation hooks ──────────────────────────────────────────

// Both mutations below route through `apiClient` -> `baseAPICall`, which
// shows a generic error toast (`notifyError(err.message)`) whenever the
// backend error has no `error_message` field — this API returns `{ detail }`,
// so it would always hit that generic path. The modal consumers show errors
// only in their own inline `<Alert>` (via `extractDocumentError`), so the
// generic toast is suppressed here to avoid a double error surface.
export function useUpdateDocument() {
  return useInvalidatingMutation('updateDocument', invalidationRegistry, {
    mutationFn: (v: { id: DocumentId; data: UpdateDocumentMutationRequest }) =>
      updateDocument({ id: v.id }, v.data, {
        disableNotifyGenericError: true,
      }),
  })
}

export function useDeleteDocument() {
  return useInvalidatingMutation('deleteDocument', invalidationRegistry, {
    mutationFn: (v: { id: DocumentId }) =>
      deleteDocument({ id: v.id }, { disableNotifyGenericError: true }),
  })
}

export function useBulkAssignDocumentBrand() {
  return useInvalidatingMutation(
    'bulkAssignDocumentBrand',
    invalidationRegistry,
    {
      mutationFn: (v: { data: BulkAssignDocumentBrandMutationRequest }) =>
        bulkAssignDocumentBrand(v.data, { disableNotifyGenericError: true }),
    }
  )
}

export function useUploadDocument() {
  return useInvalidatingMutation('uploadDocument', invalidationRegistry, {
    mutationFn: (v: {
      params: UploadDocumentParams
      onProgress?: (n: number) => void
    }) => uploadDocumentRequest(v.params, v.onProgress),
  })
}

export function useReplaceDocument() {
  return useInvalidatingMutation('replaceDocument', invalidationRegistry, {
    mutationFn: (v: {
      id: DocumentId
      params: ReplaceDocumentParams
      onProgress?: (n: number) => void
    }) => replaceDocumentRequest(v.id, v.params, v.onProgress),
  })
}

// NOTE (controller trim): tag create/update/delete hooks intentionally omitted —
// unused by the 12 documentsApi consumers; see the invalidation.ts note.
